import { cn } from "@/lib/utils";
import type { ReaderAiBlock, ReaderAiMessage } from "@/types/agentic-chat";
import { captureToofiAnalytics } from "@/lib/toofi-analytics";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ReaderAiBlockRenderer } from "./block-renderer";
import { ReaderAiSourceRibbon } from "./source-ribbon";
import type { GuidedLessonControlInput, GuidedLessonSubmitInput } from "@/types/student-toofi";

type ReaderAiMessageCardProps = {
  message: ReaderAiMessage;
  surfaceKind?: "reader" | "workspace";
  focusMode?: boolean;
  isPhone?: boolean;
  isTablet?: boolean;
  animateAssistantText?: boolean;
  onGoToPage?: (pageNumber: number) => void;
  onEditMessage?: (messageId: string, text: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onMcqSubmit: (
    blockId: string,
    selectedIndex: number,
    question?: {
      id: string;
      prompt: string;
      options: { id: string; label: string }[];
      correctIndex?: number | null;
      attemptId?: string | null;
      multi?: boolean;
    },
  ) => void;
  onFollowUpPrompt?: (prompt: string) => void;
  onEssayStart: (blockId: string, prompt: string, minWords?: number, promptId?: string) => void;
  onEssayDraftChange: (blockId: string, draft: string) => void;
  onEssayAttachmentAdd?: (blockId: string, file: File) => void;
  onEssayAttachmentRemove?: (blockId: string) => void;
  onEssayReadbackConfirm?: (blockId: string, confirmedText: string) => void;
  onEssaySubmit: (blockId: string, prompt: string, draft: string, confirmedText?: string | null) => void;
  onGuidedLessonSubmit?: (input: GuidedLessonSubmitInput) => void;
  onGuidedLessonContinue?: (input: GuidedLessonControlInput) => void;
  onGuidedLessonSkip?: (input: GuidedLessonControlInput) => void;
  onPlanSave?: (messageId: string, blockId: string) => void;
  onPlanRefine?: (messageId: string, blockId: string) => void;
};

export function ReaderAiMessageCard({
  message,
  surfaceKind = "reader",
  focusMode = false,
  isPhone = false,
  isTablet = false,
  animateAssistantText = false,
  onGoToPage,
  onEditMessage,
  onDeleteMessage,
  onMcqSubmit,
  onFollowUpPrompt,
  onEssayStart,
  onEssayDraftChange,
  onEssayAttachmentAdd,
  onEssayAttachmentRemove,
  onEssayReadbackConfirm,
  onEssaySubmit,
  onGuidedLessonSubmit,
  onGuidedLessonContinue,
  onGuidedLessonSkip,
  onPlanSave,
  onPlanRefine,
}: ReaderAiMessageCardProps) {
  const isAssistant = message.role === "assistant";
  const isUser = message.role === "user";
  const isSystem = message.role === "system";
  const userContextLabel = useMemo(() => {
    if (!isUser || message.usedContextScope === "general_scope") return null;
    if (typeof message.contextLabel === "string" && message.contextLabel.trim()) {
      return message.contextLabel.trim();
    }
    if (message.source?.pageStart) {
      return `This selection - p.${message.source.pageStart}`;
    }
    return null;
  }, [isUser, message.contextLabel, message.source?.pageStart, message.usedContextScope]);
  const hasGuidedLessonBlocks = useMemo(
    () =>
      message.blocks.some(
        (block) => block.type === "guided_step" || block.type === "guided_step_feedback",
      ),
    [message.blocks],
  );
  const hasArtifactHeavyBlocks = useMemo(
    () =>
      message.blocks.some((block) =>
        !["markdown", "explanation", "feedback", "source"].includes(block.type),
      ),
    [message.blocks],
  );
  const editableText = useMemo(() => {
    const firstBlock = message.blocks[0];
    if (message.role !== "user" || !firstBlock || firstBlock.type !== "markdown") return null;
    return firstBlock.markdown;
  }, [message.blocks, message.role]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(editableText ?? "");
  const runtimeMetaLabel = useMemo(() => {
    if (!message.runtimeMeta) return null;
    if (message.runtimeMeta.fallbackUsed) return "Fallback used";
    if (message.runtimeMeta.normalized) return "Repaired for clarity";
    if (message.runtimeMeta.retryUsed) return "Retried for reliability";
    return null;
  }, [message.runtimeMeta]);
  const animatedCharactersTarget = useMemo(
    () =>
      message.blocks.reduce((total, block) => {
        if (block.type === "markdown") return total + block.markdown.length;
        if (block.type === "explanation") return total + block.text.length;
        if (block.type === "feedback") return total + block.text.length;
        return total;
      }, 0),
    [message.blocks],
  );
  const [revealedCharacters, setRevealedCharacters] = useState(animateAssistantText ? 0 : animatedCharactersTarget);

  useEffect(() => {
    if (!isAssistant || !animateAssistantText || animatedCharactersTarget <= 0) {
      setRevealedCharacters(animatedCharactersTarget);
      return;
    }
    setRevealedCharacters(0);
    const interval = window.setInterval(() => {
      setRevealedCharacters((current) => {
        if (current >= animatedCharactersTarget) {
          window.clearInterval(interval);
          return animatedCharactersTarget;
        }
        return Math.min(animatedCharactersTarget, current + Math.max(4, Math.ceil(animatedCharactersTarget / 40)));
      });
    }, 26);
    return () => window.clearInterval(interval);
  }, [animateAssistantText, animatedCharactersTarget, isAssistant, message.id]);

  useEffect(() => {
    if (!isAssistant || !runtimeMetaLabel) return;
    captureToofiAnalytics("toofi_fallback_meta_viewed", {
      message_id: message.id,
      surface_kind: surfaceKind,
      fallback_used: message.runtimeMeta?.fallbackUsed ?? false,
      normalized: message.runtimeMeta?.normalized ?? false,
      retry_used: message.runtimeMeta?.retryUsed ?? false,
      quality_severity: message.runtimeMeta?.qualitySeverity ?? null,
    });
  }, [isAssistant, message.id, message.runtimeMeta?.fallbackUsed, message.runtimeMeta?.normalized, message.runtimeMeta?.qualitySeverity, message.runtimeMeta?.retryUsed, runtimeMetaLabel, surfaceKind]);

  const renderedBlocks = useMemo(() => {
    if (!isAssistant || !animateAssistantText || revealedCharacters >= animatedCharactersTarget) {
      return message.blocks;
    }

    let remaining = revealedCharacters;
    return message.blocks.map((block): ReaderAiBlock => {
      if (block.type === "markdown") {
        const markdown = block.markdown.slice(0, Math.max(0, remaining));
        remaining -= block.markdown.length;
        return { ...block, markdown };
      }
      if (block.type === "explanation") {
        const text = block.text.slice(0, Math.max(0, remaining));
        remaining -= block.text.length;
        return { ...block, text };
      }
      if (block.type === "feedback") {
        const text = block.text.slice(0, Math.max(0, remaining));
        remaining -= block.text.length;
        return { ...block, text };
      }
      return block;
    });
  }, [animateAssistantText, animatedCharactersTarget, isAssistant, message.blocks, revealedCharacters]);

  const handleSave = () => {
    const trimmed = draft.trim();
    if (!trimmed || !onEditMessage) return;
    onEditMessage(message.id, trimmed);
    setEditing(false);
  };

  return (
    <div
      className={cn(
        "group animate-in fade-in-0 slide-in-from-bottom-1 transition-all duration-150",
        isUser
          ? cn(
              "ml-auto flex flex-col items-end",
              isPhone
                ? "max-w-[96%]"
                : isTablet
                  ? "max-w-[90%]"
                  : "max-w-[88%] xl:max-w-[80%]",
              focusMode && !isTablet && "xl:max-w-[74%]",
            )
          : isSystem
            ? cn(
                "rounded-[1rem] border border-transparent bg-transparent px-0 py-0 shadow-none",
                isPhone ? "max-w-[96%]" : isTablet ? "max-w-[90%]" : "max-w-[88%] xl:max-w-[84%]",
              )
            : cn(
                isPhone ? "max-w-[96%]" : isTablet ? "max-w-[90%]" : "max-w-[88%] xl:max-w-[84%]",
                hasArtifactHeavyBlocks
                  ? "w-full rounded-[1rem] border border-transparent bg-transparent px-0 py-0 shadow-none"
                  : cn(
                      "w-fit border border-slate-200/45 bg-[linear-gradient(180deg,rgba(255,255,255,0.72),rgba(248,250,252,0.56))] shadow-[0_10px_30px_rgba(148,163,184,0.08)] backdrop-blur-xl",
                      isPhone
                        ? "rounded-[1rem] px-2.5 py-2"
                        : isTablet
                          ? "rounded-[1.05rem] px-3 py-2.5"
                          : "rounded-[1.15rem] px-3.5 py-2.5 xl:px-4 xl:py-3",
                    ),
                focusMode && !isTablet && "xl:max-w-[78%]",
              ),
      )}
    >
      {isAssistant ? <ReaderAiSourceRibbon sourceOrigin={message.sourceOrigin} memoryUsage={message.memoryUsage} compact={isPhone || isTablet} /> : null}
      {isAssistant && runtimeMetaLabel ? (
        <div className="mt-1 flex justify-start">
          <span className="inline-flex items-center rounded-full border border-slate-200/80 bg-white/84 px-2.5 py-1 text-[10px] font-medium text-slate-500 shadow-[0_10px_24px_rgba(148,163,184,0.10)] backdrop-blur-xl">
            {runtimeMetaLabel}
          </span>
        </div>
      ) : null}
      <div
        className={cn(
          isUser && cn(
            "w-fit border border-white/65 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(241,245,249,0.92))] text-slate-900 shadow-[0_18px_45px_rgba(148,163,184,0.18)] backdrop-blur-2xl",
            isPhone
              ? "rounded-[1.15rem] px-3 py-2.5"
              : isTablet
                ? "rounded-[1.25rem] px-3.5 py-[0.6875rem]"
                : "rounded-[1.45rem] px-4 py-3 xl:rounded-[1.55rem] xl:px-[1.125rem]",
          ),
          isAssistant && hasArtifactHeavyBlocks && "rounded-none border-0 bg-transparent p-0 shadow-none backdrop-blur-0",
        )}
      >
        {editing && editableText ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="min-h-[96px] rounded-[1rem] border-white/70 bg-white/85 text-[13px] leading-6"
            />
          </div>
        ) : (
          <div className={cn("space-y-2", isSystem && "space-y-1", hasGuidedLessonBlocks && "space-y-1.5")}>
            {renderedBlocks.map((block, blockIndex) => (
              <div key={`${message.id}-${block.type}-${blockIndex}`}>
                <ReaderAiBlockRenderer
                  messageId={message.id}
                  threadId={message.threadId}
                  blockKey={`${message.id}-${block.type}-${blockIndex}`}
                  surfaceKind={surfaceKind}
                  block={block}
                  focusMode={focusMode}
                  onGoToPage={onGoToPage}
                  onMcqSubmit={onMcqSubmit}
                  onFollowUpPrompt={onFollowUpPrompt}
                  onEssayStart={onEssayStart}
                  onEssayDraftChange={onEssayDraftChange}
                  onEssayAttachmentAdd={onEssayAttachmentAdd}
                  onEssayAttachmentRemove={onEssayAttachmentRemove}
                  onEssayReadbackConfirm={onEssayReadbackConfirm}
                  onEssaySubmit={onEssaySubmit}
                  onGuidedLessonSubmit={onGuidedLessonSubmit}
                  onGuidedLessonContinue={onGuidedLessonContinue}
                  onGuidedLessonSkip={onGuidedLessonSkip}
                  onPlanSave={onPlanSave}
                  onPlanRefine={onPlanRefine}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {isUser && userContextLabel ? (
        <div className="mt-1 flex justify-end">
          <span
            className={cn(
              "inline-flex items-center rounded-full border border-slate-200/75 bg-white/84 px-2.5 py-1 text-[10px] font-medium text-slate-500 shadow-[0_10px_24px_rgba(148,163,184,0.12)] backdrop-blur-xl",
              isPhone && "px-2 py-0.5 text-[9px]",
            )}
          >
            {userContextLabel}
          </span>
        </div>
      ) : null}
      {isUser && editableText ? (
        <div className={cn(
          "relative z-10 mt-1.5 flex justify-end gap-1 rounded-full bg-white/82 px-1.5 py-1 shadow-[0_10px_28px_rgba(148,163,184,0.16)] backdrop-blur-xl opacity-100 transition-opacity duration-150 pointer-events-auto",
          isPhone && "mt-1 gap-0.5 px-1 py-0.5 shadow-[0_8px_18px_rgba(148,163,184,0.12)]",
        )}>
          {editing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn("rounded-full bg-white/90 text-slate-600 shadow-sm hover:bg-white hover:text-slate-900", isPhone ? "h-6 w-6" : "h-7 w-7")}
                onClick={handleSave}
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn("rounded-full bg-white/90 text-slate-500 shadow-sm hover:bg-white hover:text-slate-900", isPhone ? "h-6 w-6" : "h-7 w-7")}
                onClick={() => {
                  setDraft(editableText);
                  setEditing(false);
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn("rounded-full bg-white/90 text-slate-500 shadow-sm hover:bg-white hover:text-slate-900", isPhone ? "h-6 w-6" : "h-7 w-7")}
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn("rounded-full bg-white/90 text-rose-600 shadow-sm hover:bg-white hover:text-rose-700", isPhone ? "h-6 w-6" : "h-7 w-7")}
                onClick={() => onDeleteMessage?.(message.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
